/** 服务端状态：所有业务数据只从后端来，前端不缓存业务状态（规格约束：
 * 账目只有一个真源，不能在界面上破）。
 *
 * 用 TanStack Query 的现成语义承接三件事：轮询、失效、**重试边界**
 * （`retry: isRetryable`——4xx 与契约违约不重试）。
 */

import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import {
  readFills,
  readOrders,
  readPositions,
  readRunDetail,
  readRuns,
  readSnapshots,
  readStatus,
} from "../api/contract";
import { isRetryable } from "../api/errors";
import type {
  OrderRecord,
  PositionRecord,
  RunDetail,
  RunRecord,
  SnapshotsPayload,
  StatusPayload,
  TradeRecord,
} from "../api/types";
import { readDataSnapshot, readP2, readHaltState, readPaperEvents, readSignals, readDailySnapshots, readAlerts, readDailyReports, readDailyReport, readEquity } from "../api/contract";
import type { DataSnapshot, HaltState, PaperEvent, SignalsPayload, DailySnapshotsPayload, AlertRecord, DailyReportsPayload, EquityPayload } from "../api/types";

/** 统一的取数选项：屏只关心数据类型，重试语义在这里定一次。 */
const READ = {
  retry: (count: number, error: unknown) => count < 2 && isRetryable(error),
  retryDelay: 1_500,
} as const;

export const keys = {
  status: ["status"] as const,
  orders: ["orders"] as const,
  fills: ["fills"] as const,
  positions: ["positions"] as const,
  runs: ["runs"] as const,
  run: (runId: string) => ["runs", runId] as const,
  snapshots: ["snapshots"] as const,
  dataSnapshot: ["data-snapshot"] as const,
};

export type ReadResult<T> = UseQueryResult<T, Error>;

/** 状态是停机横条、顶栏与总览的共同来源，轮询最勤。 */
export function useStatus(): ReadResult<StatusPayload> {
  return useQuery<StatusPayload, Error>({
    queryKey: keys.status,
    queryFn: () => readStatus(),
    refetchInterval: 10_000,
    ...READ,
  });
}

export function useOrders(): ReadResult<OrderRecord[]> {
  return useQuery<OrderRecord[], Error>({ queryKey: keys.orders, queryFn: () => readOrders(), ...READ });
}

export function useFills(): ReadResult<TradeRecord[]> {
  return useQuery<TradeRecord[], Error>({ queryKey: keys.fills, queryFn: () => readFills(), ...READ });
}

export function usePositions(): ReadResult<PositionRecord[]> {
  return useQuery<PositionRecord[], Error>({ queryKey: keys.positions, queryFn: () => readPositions(), ...READ });
}

export function useRuns(): ReadResult<RunRecord[]> {
  return useQuery<RunRecord[], Error>({ queryKey: keys.runs, queryFn: () => readRuns(), ...READ });
}

/** 快照清单是研究侧产出的目录快照，不会自己变；只按统一的重试语义取一次，不轮询。 */
export function useSnapshots(): ReadResult<SnapshotsPayload> {
  return useQuery<SnapshotsPayload, Error>({
    queryKey: keys.snapshots,
    queryFn: () => readSnapshots(),
    ...READ,
  });
}

export function useRunDetail(runId: string | null): ReadResult<RunDetail> {
  return useQuery<RunDetail, Error>({
    queryKey: keys.run(runId ?? ""),
    queryFn: () => readRunDetail(runId as string),
    enabled: Boolean(runId),
    ...READ,
  });
}

export function useDataSnapshot(): ReadResult<DataSnapshot> {
  return useQuery<DataSnapshot, Error>({
    queryKey: keys.dataSnapshot,
    queryFn: () => readDataSnapshot(),
    ...READ,
  });
}

export function useP2(path: string, params?: Record<string, string | number | boolean>): ReadResult<import("../api/contract").P2Payload> {
  return useQuery<import("../api/contract").P2Payload, Error>({ queryKey: ["p2", path, params], queryFn: () => readP2(path, params), ...READ });
}
const q=<T,>(key: readonly unknown[], fn:()=>Promise<T>): ReadResult<T> => useQuery<T,Error>({queryKey:key,queryFn:fn,...READ});
export const useHaltState=()=>q<HaltState>(["paper-state"],readHaltState);
export const usePaperEvents=()=>q<PaperEvent[]>(["paper-events"],readPaperEvents);
export const useSignals=(status?:string)=>q<SignalsPayload>(["signals",status],()=>readSignals(status));
export const useDailySnapshots=()=>q<DailySnapshotsPayload>(["daily-snapshots"],readDailySnapshots);
export const useAlerts=()=>q<AlertRecord[]>(["alerts"],readAlerts);
export const useDailyReports=()=>q<DailyReportsPayload>(["daily-reports"],readDailyReports);
export const useDailyReport=(date:string)=>q<import("../api/types").DailyReport>(["daily-report",date],()=>readDailyReport(date));
export const useEquity=(strategy:string)=>q<EquityPayload>(["equity",strategy],()=>readEquity(strategy));
